import { CircleCheck, Download, EllipsisVertical, MonitorDown, Share, SquarePlus } from 'lucide-react';
import { useSyncExternalStore, type ReactNode } from 'react';
import { Alert } from '../components/Alert';
import { Badge } from '../components/Badge';
import { Button, ButtonLink } from '../components/Button';
import { PageBody } from '../components/PageBody';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';
import { isIos, isStandalone } from '../lib/push';
import { canInstall, promptInstall, subscribeInstall } from '../lib/pwa';
import styles from './InstallPage.module.css';

type Platform = 'android' | 'ios' | 'desktop';

function detectPlatform(): Platform {
  if (isIos()) return 'ios';
  if (/Android/i.test(navigator.userAgent)) return 'android';
  return 'desktop';
}

function Steps({ children }: { children: ReactNode }) {
  return <ol className={styles.steps}>{children}</ol>;
}

/** An inline picture of the button the step talks about. */
function Glyph({ icon: Icon, label }: { icon: typeof Share; label: string }) {
  return (
    <span className={styles.glyph}>
      <Icon aria-hidden="true" />
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

const GUIDES: Record<Platform, { title: string; body: ReactNode }> = {
  android: {
    title: 'Telefon lub tablet z Androidem',
    body: (
      <Steps>
        <li>Otwórz tę stronę w przeglądarce Chrome.</li>
        <li>
          Stuknij menu <Glyph icon={EllipsisVertical} label="trzy kropki" /> w prawym górnym rogu.
        </li>
        <li>Wybierz „Zainstaluj aplikację” albo „Dodaj do ekranu głównego” i potwierdź.</li>
        <li>Otwórz aplikację „Wizyty” z ikony na ekranie głównym.</li>
        <li>W Ustawieniach stuknij „Włącz powiadomienia” i zezwól na nie.</li>
      </Steps>
    ),
  },
  ios: {
    title: 'iPhone i iPad',
    body: (
      <>
        <Steps>
          <li>Otwórz tę stronę w Safari.</li>
          <li>
            Stuknij przycisk Udostępnij <Glyph icon={Share} label="kwadrat ze strzałką w górę" /> (na dole ekranu albo obok
            paska adresu).
          </li>
          <li>
            Przewiń listę i wybierz „Do ekranu początkowego” <Glyph icon={SquarePlus} label="kwadrat z plusem" />, potem
            „Dodaj”.
          </li>
          <li>Otwórz aplikację „Wizyty” z ikony na ekranie początkowym.</li>
          <li>W Ustawieniach stuknij „Włącz powiadomienia” i zezwól na nie.</li>
        </Steps>
        <p className={styles.note}>
          Na iPhonie i iPadzie przypomnienia działają tylko w aplikacji dodanej do ekranu początkowego i wymagają iOS
          16.4 lub nowszego (Ustawienia → Ogólne → Uaktualnienia).
        </p>
      </>
    ),
  },
  desktop: {
    title: 'Komputer',
    body: (
      <Steps>
        <li>Otwórz tę stronę w Chrome albo Edge.</li>
        <li>
          Kliknij ikonę instalacji <Glyph icon={MonitorDown} label="monitor ze strzałką" /> na końcu paska adresu (albo
          menu <Glyph icon={EllipsisVertical} label="trzy kropki" /> → „Zainstaluj Kalendarz wizyt”).
        </li>
        <li>Aplikacja otworzy się w osobnym oknie – można ją przypiąć do paska zadań.</li>
      </Steps>
    ),
  },
};

export function InstallPage() {
  const platform = detectPlatform();
  const installable = useSyncExternalStore(subscribeInstall, canInstall);
  const installed = isStandalone();
  const order: Platform[] = [platform, ...(['android', 'ios', 'desktop'] as const).filter((p) => p !== platform)];

  return (
    <>
      <PageHeader title="Instalacja aplikacji" subtitle="Ikona na ekranie głównym i przypomnienia o wizytach" />
      <PageBody>
        {installed ? (
          <Alert kind="success" title="Aplikacja jest zainstalowana na tym urządzeniu">
            <p>Przypomnienia włączysz w Ustawieniach.</p>
            <p className={styles.alertAction}>
              <ButtonLink to="/ustawienia" variant="secondary">
                Przejdź do ustawień
              </ButtonLink>
            </p>
          </Alert>
        ) : (
          <p className={styles.lead}>
            Po dodaniu do ekranu głównego kalendarz otwiera się jak zwykła aplikacja: z własnej ikony, na pełnym ekranie,
            także bez zasięgu (do przeglądania). Tylko wtedy na iPhonie działają przypomnienia.
          </p>
        )}

        {installable && !installed && (
          <div>
            <Button variant="primary" size="lg" icon={<Download aria-hidden="true" />} onClick={() => void promptInstall()}>
              Zainstaluj aplikację
            </Button>
          </div>
        )}

        {order.map((p) => (
          <Section key={p} title={GUIDES[p].title}>
            {p === platform && !installed && (
              <p className={styles.thisDevice}>
                <Badge tone="accent">
                  <CircleCheck aria-hidden="true" /> To urządzenie
                </Badge>
              </p>
            )}
            {GUIDES[p].body}
          </Section>
        ))}
      </PageBody>
    </>
  );
}
